<template>
  <!-- KDS Layout - Only shown when useKDS is true -->
  <div v-if="useKDS" class="main-container">
    <!-- Summary section with relative positioning -->
    <div class="summary-section">
      <SummaryKDS :orders="orders" :restaurant="restaurant" />
    </div>
    
    <!-- Separator line -->
    <div class="separator-line"></div>
    
    <!-- Orders container -->
    <div class="orders-container">
      <!-- No orders message with i18n -->
      <div v-if="!orders.length" class="no-orders-message">
        {{ $t('message.noOrders') }}
      </div>
      
      <div v-else class="orders-scroll-container">
        <button
          v-for="order in filteredOrders"
          :key="order.id"
          :class="[getOrderClass(order), { 'rider-waiting': hasRiderArrived(order), 'rider-urgent': hasRiderArrived(order) && getRiderMinutesWaiting(order) >= 10, 'rbe-order': isRbeOrderButton }]"
          class="order-button"
          data-testid="order-card"
          :data-verdict="getOrderClass(order).replace('-order', '') || 'none'"
          @click="selectOrder(order)"
        >
          <span v-if="isPendingAnalysis(order)" class="order-pending-badge" :title="$t('message.analysisQueuedOffline')">
            <i class="pi pi-clock"></i>
          </span>
          <div v-if="!isRbeOrderButton && hasRiderArrived(order)" class="rider-badge">
            <svg class="rider-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7zM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1zm10-2c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>
            <span class="rider-label">{{ $t('message.riderIsHere') }}</span>
            <span v-if="getRiderMinutesWaiting(order) >= 1" class="rider-wait-time">{{ getRiderWaitingTime(order) }}</span>
          </div>
          <div v-if="isRbeOrderButton" class="rbe-content">
            <img
              v-if="order.deliveryPlatform && getPlatformIcon(order.deliveryPlatform)"
              :src="getPlatformIcon(order.deliveryPlatform)"
              :alt="order.deliveryPlatform"
              class="rbe-platform-icon"
            />
            <div class="rbe-main">
              <div class="rbe-code" translate="no">{{ getOrderCode(order) }}</div>
              <!-- Burger King: delivery address (location-pin). Street fades on overflow; the house
                   number stays visible. Falls through to the name when no address has arrived yet. -->
              <div v-if="rbeAddressParts(order).full" class="rbe-address">
                <svg class="rbe-pin-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>
                <span class="rbe-address-text" :title="rbeAddressParts(order).full" translate="no"><span class="rbe-address-head" ref="rbeAddressHead">{{ rbeAddressParts(order).head }}</span><span class="rbe-address-tail" ref="rbeAddressTail">{{ rbeAddressParts(order).tail }}</span></span>
              </div>
              <!-- Popeyes (always) and Burger King fallback: customer name with a person icon -->
              <div v-else-if="getRecipientName(order)" class="rbe-name" translate="no">
                <svg class="rbe-person-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                <span>{{ getRecipientName(order) }}</span>
              </div>
            </div>
            <div class="rbe-meta">
              <div class="rbe-time" :class="{ 'time-delay': getMinutesAgo(order.createdAt) > 10 }">{{ rbeTimeAgo(order.createdAt) }}</div>
              <div v-if="order.photoCount > 0" :class="['rbe-photos', getOrderClass(order)]">{{ order.photoCount }} {{ $t('message.photo') }}<span v-if="order.photoCount > 1">s</span></div>
            </div>
          </div>
          <div v-else class="order-content">
            <img
              v-if="order.deliveryPlatform && getPlatformIcon(order.deliveryPlatform)"
              :src="getPlatformIcon(order.deliveryPlatform)"
              :alt="order.deliveryPlatform"
              class="platform-icon"
            />
            <div v-if="order.brandByBrand" class="brand-name" translate="no">
              {{ order.brandByBrand.name }}
            </div>
            <div class="order-id-wrapper">
              <div class="order-id" ref="orderId" translate="no">{{ getOrderDisplayText(order) }}</div>
              <TicketComponentKDS
                :order="order"
                :restaurant="restaurant"
                @item-clicked="handleTicketItemClick"
                :key="`ticket-${order.id}-${JSON.stringify(order.orderContent)}`"
              />
              <div class="order-bottom-info">
                <div class="time-ago" :class="{ 'time-delay': getMinutesAgo(order.createdAt) > 10 }">
                  {{ getTimeAgo(order.createdAt) }}
                </div>
                <div v-if="order.photoCount > 0"
                    :class="['photo-count', getOrderClass(order)]"
                >
                  {{ order.photoCount }} {{ $t('message.photo') }}<span v-if="order.photoCount > 1">s</span>
                </div>
              </div>
            </div>
          </div>
        </button>
        <button class="order-button add-manual-order-button" data-testid="add-manual-order" @click="$emit('add-manual-order')">
          <div class="order-content add-manual-content">
            <i class="pi pi-plus add-manual-icon"></i>
            <span class="add-manual-label">{{ $t('message.manualOrderAddFull') }}</span>
          </div>
        </button>
      </div>
    </div>
  </div>

  <!-- Classic Layout - Only shown when useKDS is false -->
  <div v-else class="orders-container classic-layout">
    <div class="orders-scroll-container">
      <button
        v-for="order in filteredOrders"
        :key="order.id"
        :class="[getOrderClass(order), { 'rider-waiting': hasRiderArrived(order), 'rider-urgent': hasRiderArrived(order) && getRiderMinutesWaiting(order) >= 10, 'rbe-order': isRbeOrderButton }]"
        class="order-button"
        data-testid="order-card"
        :data-verdict="getOrderClass(order).replace('-order', '') || 'none'"
        @click="selectOrder(order)"
      >
        <!-- The card on the design system (src/ui/lane.js, `ui-system-lane`): three rows that fall the same
             on every card, so the codes line up along the tray (docs/sistema-diseno/forma-y-tamano.md, «La
             tarjeta de pedido»). At the top the platform and the state's glyph — never the colour alone
             (WCAG 1.4.1); in the middle the code; at the foot «the clock», which becomes the rider's red
             band while one waits. An order still to photograph has no glyph: nothing there says «not done». -->
        <template v-if="systemLane && !isRbeOrderButton">
          <span class="card-top">
            <img
              v-if="order.deliveryPlatform && getPlatformIcon(order.deliveryPlatform)"
              :src="getPlatformIcon(order.deliveryPlatform)"
              :alt="order.deliveryPlatform"
              class="card-platform"
            />
            <!-- One call per card and render (getOrderClass runs inside): the list re-renders on every event. -->
            <template v-for="glyph in [stateGlyph(order)]" :key="glyph ? glyph.label : 'none'">
              <BzIcon v-if="glyph && glyph.icon" :name="glyph.icon" :label="$t(glyph.label)" class="card-state" />
              <span v-else-if="glyph" class="card-state-name">{{ $t(glyph.label) }}</span>
            </template>
          </span>
          <span class="order-id-wrapper card-code">
            <span class="order-id" ref="orderId" translate="no">{{ getOrderDisplayText(order) }}</span>
          </span>
          <span v-if="hasRiderArrived(order)" class="card-rider">
            <BzIcon name="delivery_dining" />
            <span class="card-rider-text">{{ $t('message.riderIsHere') }}<template v-if="getRiderMinutesWaiting(order) >= 1"> · {{ getRiderWaitingTime(order) }}</template></span>
          </span>
          <span v-else class="card-clock">
            <span class="card-time" :class="{ 'is-late': getMinutesAgo(order.createdAt) > 10 }"><BzIcon v-if="getMinutesAgo(order.createdAt) > 10" name="schedule" />{{ getTimeAgo(order.createdAt) }}</span>
            <span v-if="order.photoCount > 0" class="card-photos"><BzIcon name="photo_camera" /><span aria-hidden="true">{{ order.photoCount }}</span><span class="card-state-name">{{ $t('message.orderPhotoCount', order.photoCount) }}</span></span>
          </span>
        </template>
        <template v-else>
        <span v-if="isPendingAnalysis(order)" class="order-pending-badge" :title="$t('message.analysisQueuedOffline')">
          <i class="pi pi-clock"></i>
        </span>
        <div v-if="!isRbeOrderButton && hasRiderArrived(order)" class="rider-badge">
          <svg class="rider-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7zM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1zm10-2c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>
          <span class="rider-label">{{ $t('message.riderIsHere') }}</span>
          <span v-if="getRiderMinutesWaiting(order) >= 1" class="rider-wait-time">{{ getRiderWaitingTime(order) }}</span>
        </div>
        <!-- RBE pilot: dedicated left-aligned layout (icon · code/name · time+photos stacked at the right) -->
        <div v-if="isRbeOrderButton" class="rbe-content">
          <img
            v-if="order.deliveryPlatform && getPlatformIcon(order.deliveryPlatform)"
            :src="getPlatformIcon(order.deliveryPlatform)"
            :alt="order.deliveryPlatform"
            class="rbe-platform-icon"
          />
          <div class="rbe-main">
            <div class="rbe-code" translate="no">{{ getOrderCode(order) }}</div>
            <!-- Burger King: delivery address (location-pin). Street fades on overflow; the house
                 number stays visible. Falls through to the name when no address has arrived yet. -->
            <div v-if="rbeAddressParts(order).full" class="rbe-address">
              <svg class="rbe-pin-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>
              <span class="rbe-address-text" :title="rbeAddressParts(order).full" translate="no"><span class="rbe-address-head" ref="rbeAddressHead">{{ rbeAddressParts(order).head }}</span><span class="rbe-address-tail" ref="rbeAddressTail">{{ rbeAddressParts(order).tail }}</span></span>
            </div>
            <!-- Popeyes (always) and Burger King fallback: customer name with a person icon -->
            <div v-else-if="getRecipientName(order)" class="rbe-name" translate="no">
              <svg class="rbe-person-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              <span>{{ getRecipientName(order) }}</span>
            </div>
          </div>
          <div class="rbe-meta">
            <div class="rbe-time" :class="{ 'time-delay': getMinutesAgo(order.createdAt) > 10 }">{{ rbeTimeAgo(order.createdAt) }}</div>
            <div v-if="order.photoCount > 0" :class="['rbe-photos', getOrderClass(order)]">{{ order.photoCount }} {{ $t('message.photo') }}<span v-if="order.photoCount > 1">s</span></div>
          </div>
        </div>
        <div v-else class="order-content">
          <img
            v-if="order.deliveryPlatform && getPlatformIcon(order.deliveryPlatform)"
            :src="getPlatformIcon(order.deliveryPlatform)"
            :alt="order.deliveryPlatform"
            class="platform-icon"
          />
          <div class="order-id-wrapper">
            <div class="order-id" ref="orderId" translate="no">{{ getOrderDisplayText(order) }}</div>
            <div class="order-bottom-info">
              <div class="time-ago" :class="{ 'time-delay': getMinutesAgo(order.createdAt) > 10 }">
                {{ getTimeAgo(order.createdAt) }}
              </div>
              <div v-if="order.photoCount > 0"
                  :class="['photo-count', getOrderClass(order)]"
              >
                {{ order.photoCount }} {{ $t('message.photo') }}<span v-if="order.photoCount > 1">s</span>
              </div>
            </div>
          </div>
        </div>
        </template>
      </button>
      <button class="order-button add-manual-order-button" data-testid="add-manual-order" @click="$emit('add-manual-order')">
        <div class="order-content add-manual-content">
          <BzIcon v-if="systemLane" name="add" class="add-manual-icon" />
          <i v-else class="pi pi-plus add-manual-icon"></i>
          <span class="add-manual-label">{{ $t('message.manualOrderAddFull') }}</span>
        </div>
      </button>
    </div>
  </div>
</template>



<script>
import Logger from '@/logger';
import TicketComponentKDS from './TicketComponentKDS.vue';
import SummaryKDS from './SummaryKDS.vue';
import posthog from 'posthog-js';
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';
import { isDecidedAnalysis, isIncorrectAnalysis } from '../verdictState';

// Types of AppResponse
const AppResponseTypes = {
  ORDER_ANALYZED: 'ORDER_ANALYZED',
  EMPTY_ORDER_CONTENT: 'EMPTY_ORDER_CONTENT',
  EMPTY_MODEL_DETECTIONS: 'EMPTY_MODEL_DETECTIONS',
};

// Order-visibility timing clocks — MUST mirror bronze.get_raw_orders_realtime (backend) so the tablet
// hides an order at the same moment the backend stops returning it: no reappear-on-refresh, no lingering.
const ORDER_VISIBILITY_TIMINGS = {
  MAX_AGE_MINUTES: 60,    // = max_age_minutes (cutoff1): global age-out for any untouched order
  DONE_GRACE_MINUTES: 1,  // = analyzed_grace_minutes: keep a done (green) order visible this long after its verdict (backend aligned to 1)
  RIDER_HOLD_MINUTES: 10, // = rider_hold_minutes: keep a done order with a waiting rider this long after check-in
};

// Types of Order Class Names
const OrderClassNames = {
  CORRECT: 'correct-order',
  INCORRECT: 'incorrect-order',
  // Photos taken but not analysed yet because there's no connection — NOT wrong, just waiting.
  // Distinct calm state + alert badge, so nobody reads an unanalysed order as a failed one.
  PENDING: 'pending-order',
  DEFAULT: ''
};

const logger = new Logger({screen: 'OrderSelectionComponent'});
export default {
  name: 'OrderSelectionComponent',
  components: {
    TicketComponentKDS,
    SummaryKDS,
    BzIcon
  },
  emits: ['order-selected', 'add-manual-order', 'mark-rider-delivered'],
  inject: ['isOnline'],
  props: {
    orders: {
      type: Array,
      required: true
    },
    restaurant: {
      type: Object,
      required: false,
      default: () => ({ proActivated: true })
    },
    useKDS: {
      type: Boolean,
      required: false,
      default: false
    },
    deliveredRiderOrderIds: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      currentTime: new Date(),
      // RBE pilot flag (Burger King / Popeyes): show a short delivery code + the customer name.
      // Off for every other client, so the button renders exactly as before. Targeted in PostHog by
      // the `client` person property (BURGER_KING / POPEYES). Read once at creation, like `ticket`.
      isRbeOrderButton: posthog.isFeatureEnabled('rbe-order-button'),
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane()
    };
  },
  mounted() {
    this.adjustFontSizes();
    this.adjustAddressFades();
    // Re-measure the order numbers and the address fades once the webfont has loaded (fallback-font
    // metrics differ, so a slow kitchen wifi would otherwise keep a scale measured with the fallback
    // and cut a long number on both sides until the list re-renders), and the fades on
    // resize/rotation — the button is mobile-first, so orientation changes matter.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.adjustFontSizes();
        this.adjustAddressFades();
      });
    }
    this._onResizeFades = () => this.adjustAddressFades();
    window.addEventListener('resize', this._onResizeFades);
    // Re-evaluate the visibility clocks every 30s so a done order hides within ~30s of its grace
    // (1 min) instead of lagging up to a full minute behind the backend (which anchors on continuous
    // timestamps). getSecondsAgo does the continuous comparison; this only controls how often it re-runs.
    this.timeUpdateInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 30000);
  },
  beforeUnmount() {
    // Clean up the timer when component is unmounted
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
    if (this._onResizeFades) {
      window.removeEventListener('resize', this._onResizeFades);
    }
  },
  updated() {
    this.adjustFontSizes();
    this.adjustAddressFades();
  },
  computed: {
      // RBE pilot: within the `rbe-order-button` flag, Burger King shows the delivery ADDRESS in the
      // button; every other RBE client (Popeyes) shows the customer NAME. `client` is the brand enum
      // (BURGER_KING / POPEYES) from fetchRestaurantDoc — the SAME value the flag is targeted by
      // (App.vue setPersonPropertiesForFlags), exact per brand: a store is identified by its device
      // tag (e.g. "burgerking-villanueva"), NOT by client, so every Burger King store shares
      // client === 'BURGER_KING'. Unknown client → name (safe default). BK also falls back to the name
      // when no address has arrived yet (template: address v-if, name v-else-if).
      rbeShowAddress() {
        return this.isRbeOrderButton && !!this.restaurant && this.restaurant.client === 'BURGER_KING';
      },
      sortedOrders() {
        return [...this.orders].sort((a, b) => {
          if (!a.order && b.order) return 1;
          if (a.order && !b.order) return -1;
          if (!a.order && !b.order) return 0;

          // RIDER PRIORITY: Orders with rider check-in come first
          const aHasRider = this.hasRiderArrived(a);
          const bHasRider = this.hasRiderArrived(b);
          if (aHasRider && !bHasRider) return -1;
          if (!aHasRider && bHasRider) return 1;
          if (aHasRider && bHasRider) {
            const aTime = this.getRiderArrivalTime(a);
            const bTime = this.getRiderArrivalTime(b);
            if (aTime && bTime) return new Date(aTime) - new Date(bTime);
          }

          // If proActivated is false, orders with photos should be treated as "correct" (sorted to the end)
          if (this.restaurant && this.restaurant.proActivated === false) {
            // If one has photos and the other doesn't
            if (a.photoCount > 0 && b.photoCount === 0) return 1;
            if (a.photoCount === 0 && b.photoCount > 0) return -1;
          }
          
          // If one has no analyses and the other does
          if (!a.order.orderAnalyses && b.order.orderAnalyses) return -1;
          if (a.order.orderAnalyses && !b.order.orderAnalyses) return 1;

          if (a.order.orderAnalyses && b.order.orderAnalyses) {
            // If one has isCorrect null and the other has isCorrect != null
            if (a.order.orderAnalyses.isCorrect === null && b.order.orderAnalyses.isCorrect !== null) return -1;
            if (a.order.orderAnalyses.isCorrect !== null && b.order.orderAnalyses.isCorrect === null) return 1;
            
            // If both have analyses, check isCorrect
            if (a.order.orderAnalyses.isCorrect === false && b.order.orderAnalyses.isCorrect === true) return -1;
            if (a.order.orderAnalyses.isCorrect === true && b.order.orderAnalyses.isCorrect === false) return 1;
          }

          return 0;
        });
      },
      filteredOrders() {
        return this.sortedOrders.filter(order => {
          if (!order.order) {
            return true;
          }

          // Marked as delivered (manually or auto) → always hide
          if (this.deliveredRiderOrderIds && this.deliveredRiderOrderIds[order.orderId]) {
            return false;
          }

          // Global age-out — mirrors the backend candidate window (cutoff1 = created_at ≥ NOW − max_age_minutes).
          // The backend gates EVERY order by this BEFORE any visibility arm, so an order past the window is
          // never returned on a refresh. Replicating it here gives each order its own clock: an untouched order
          // (no photo, no rider, no verdict — the KFC Goussainville case) self-expires instead of lingering on
          // an always-on tablet until a manual refresh. Done / rider-handled orders are already hidden earlier
          // by the branches below (well within the window); this only bounds the "show forever" paths.
          if (this.getSecondsAgo(order.createdAt) > ORDER_VISIBILITY_TIMINGS.MAX_AGE_MINUTES * 60) {
            return false;
          }

          // "Done" = the order no longer needs attention. With AI on that's isCorrectOrder
          // (analysis verdict / manualIsCorrect). With AI OFF there is no analysis, so a photographed
          // order is operationally done — this mirrors the green color in getOrderClass
          // (proActivated===false && photoCount>0) and the backend, which stops returning CV-off
          // orders ~2 min after the photo. Without treating it as done, an AI-off green card has no
          // age-out path and stays on the tablet forever (the KFC Paris bug).
          const aiOff = this.restaurant && this.restaurant.proActivated === false;
          const isDone = this.isCorrectOrder(order.order) || (aiOff && order.photoCount > 0);
          const hasRider = this.hasRiderArrived(order);

          // Rider waiting + not done → always show
          if (hasRider && !isDone) {
            return true;
          }

          // Rider waiting + done → show for 10 min after rider arrived, then hide
          if (hasRider && isDone) {
            const riderArrival = this.getRiderArrivalTime(order);
            if (!riderArrival) return true;
            return this.getSecondsAgo(riderArrival) <= ORDER_VISIBILITY_TIMINGS.RIDER_HOLD_MINUTES * 60;
          }

          // No rider + not done → show, bounded by the global age-out gate above (which hides it once it
          // leaves the backend window). Before that gate this branch had no clock and lingered forever —
          // the KFC Goussainville symptom.
          if (!isDone) {
            return true;
          }

          // No rider + done → hide shortly after it became done. Anchor on the analysis's updatedAt
          // (falling back to createdAt) to match the backend grace, which anchors on
          // COALESCE(updated_at, created_at): on a re-analysis the two timestamps diverge and the
          // front would otherwise hide earlier than the back kept the card. Fall back further to the
          // order's own timestamps so AI-off orders (no orderAnalyses) still age out.
          const analysis = order.order.orderAnalyses;
          const doneAt = analysis?.updatedAt || analysis?.createdAt || order.processedAt || order.createdAt;
          if (!doneAt) {
            return true;
          }
          return this.getSecondsAgo(doneAt) < ORDER_VISIBILITY_TIMINGS.DONE_GRACE_MINUTES * 60;
        });
      }
  },
  methods: {
    selectOrder(order) {
      // Green order with rider waiting → tapping marks it as delivered (no camera)
      if (order.order && this.isCorrectOrder(order.order) && this.hasRiderArrived(order)) {
        this.$emit('mark-rider-delivered', order);
        return;
      }
      this.$emit('order-selected', order);
    },
    getPlatformIcon(platform) {
      try {
        return require(`@/assets/delivery_platforms/${platform}.png`);
      } catch (error) {
        return require(`@/assets/delivery_platforms/OTHERS.png`);
      }
    },
    adjustFontSizes() {
      if (!this.$refs.orderId) return;

      // Handle both single element and array of elements
      const orderIds = Array.isArray(this.$refs.orderId)
        ? this.$refs.orderId
        : [this.$refs.orderId];

      // Batch ALL layout reads first, then ALL style writes. Interleaving a read (offsetWidth/
      // scrollWidth) after a write (--scale) forces a synchronous re-layout on every card, i.e.
      // O(n) forced reflows per re-render — a dominant cost with many orders on low-end tablets.
      // Reading up-front lets the browser satisfy every read from a single layout pass.
      const scales = orderIds.map(orderId =>
        Math.max(0.4, Math.min(1, orderId.parentElement.offsetWidth / orderId.scrollWidth))
      );
      orderIds.forEach((orderId, i) => {
        orderId.style.setProperty('--scale', scales[i]);
      });
    },
    // The state's glyph on a card of the design system: its icon and its word for a screen reader.
    // None for an order still to photograph: a card with nothing there already says «not done».
    stateGlyph(order) {
      switch (this.getOrderClass(order)) {
        case OrderClassNames.CORRECT: return { icon: 'check_circle', label: 'message.orderStateCorrect' };
        // No mark on the orange card (Adrián, 14-oct-2026: «no aporta nada y ensucia el botón»): its colour
        // and its place in the tray say it. The state is still named, for a screen reader only.
        case OrderClassNames.INCORRECT: return { icon: null, label: 'message.orderStateWrong' };
        case OrderClassNames.PENDING: return { icon: 'schedule', label: 'message.orderStateQueued' };
        default: return null;
      }
    },
    getOrderDisplayText(order) {
      // Memory optimization: Don't log individual orders on every render
      // logger.console('getOrderDisplayText - Order:')
      // logger.console(order)
      if (order.ticketId) {
        return order.ticketId;
      } else if (order.deliveryId) {
        return order.deliveryId;
      } else {
        return order.posId || this.$t('message.unknown');
      }
    },
    getOrderCode(order) {
      // RBE pilot: show a short, scannable code — the last 5 chars of the delivery id (Glovo 12→5,
      // Uber 5→5). Falls back to the normal display text when the flag is off or there is no
      // deliveryId (own-delivery / manual orders), so nothing changes for other clients.
      if (this.isRbeOrderButton && order.deliveryId) {
        const id = String(order.deliveryId);
        return id.length > 5 ? id.slice(-5) : id;
      }
      return this.getOrderDisplayText(order);
    },
    getRecipientName(order) {
      // RBE pilot: customer name next to the code. `recipient_name` is a single free-form string and the
      // backend may already send it cropped (e.g. "Adrián M."). Keep it short and idempotent: first name +
      // initial of the next token (a trailing dot is normalised, not duplicated), or the single token
      // as-is, with an ellipsis backstop. Empty when the flag is off or there is no name. Shown for
      // Popeyes always, and for Burger King as a fallback until the address arrives (template decides).
      if (!this.isRbeOrderButton) return '';
      // Read from top-level (fetch / getRawOrdersRealtime) OR the nested order object, so a realtime
      // event that carries recipientName under `order` still shows the name without a refresh.
      const rawName = order.recipientName ?? (order.order && order.order.recipientName);
      const raw = (rawName || '').trim().replace(/\s+/g, ' ');
      if (!raw) return '';
      const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);
      const parts = raw.split(' ');
      let first = cap(parts[0]);
      if (first.length > 14) first = first.slice(0, 13) + '…';
      if (parts.length === 1) return first;
      const initial = (parts[1].replace(/[^0-9A-Za-zÀ-ÿ]/g, '').charAt(0) || '').toUpperCase();
      return initial ? `${first} ${initial}.` : first;
    },
    rbeAddressParts(order) {
      // RBE pilot (Burger King): the delivery address, split into a truncatable street (head) and a
      // house-number tail that stays visible. Read from top-level (fetch / getRawOrdersRealtime) OR the
      // nested order object, mirroring getRecipientName so a realtime event carrying it under `order`
      // shows without a refresh. Empty parts when not in address mode or the order has no address —
      // the address block then simply doesn't render, and Burger King falls back to the name.
      if (!this.rbeShowAddress) return { full: '', head: '', tail: '' };
      const raw = order.recipientAddress ?? (order.order && order.order.recipientAddress);
      const full = (raw || '').trim().replace(/\s+/g, ' ');
      if (!full) return { full: '', head: '', tail: '' };
      // Portal number = the first standalone number that is NOT part of the street name (skip
      // date-streets like "8 de Marzo" / "11 d'Octubre") and NOT a postal code (5 digits). Head = the
      // street before it (truncable), tail = from the number on, with a trailing CP+city pruned so the
      // tail can't balloon. No portal (e.g. "Plaça del Sol") → all head. The CSS truncates visually and
      // keeps the number (tail start) readable; the tail's leading space (white-space:pre) separates it.
      for (const m of full.matchAll(/[\s,]+(?=\d)/g)) {
        const after = full.slice(m.index + m[0].length);
        if (/^\d{1,2}\s+d[e'’]/i.test(after)) continue;   // "8 de Marzo", "11 d'Octubre"
        if (/^\d{5}(\D|$)/.test(after)) continue;             // postal code, not a portal number
        return { full, head: full.slice(0, m.index), tail: ' ' + after.replace(/,?\s*\d{5}\b.*$/, '') };
      }
      return { full, head: full, tail: '' };
    },
    adjustAddressFades() {
      // RBE pilot (Burger King): fade the street out on the right when it overflows (instead of an
      // ellipsis, which leaves a gap before the house number). Toggle `.rbe-trunc` where the text
      // overflows its box. Uses refs (NOT this.$el): with the root-level comments in this template the
      // component root is a fragment in dev builds, so $el would be an empty Text node (no
      // querySelectorAll) and this would silently no-op. Refs point at the spans of the active layout
      // (v-for exposes them as arrays), same approach as adjustFontSizes with orderId. Reads are
      // batched before writes to avoid per-element forced reflow.
      if (!this.rbeShowAddress) return;
      const asArray = (r) => (Array.isArray(r) ? r : r ? [r] : []);
      const els = [...asArray(this.$refs.rbeAddressHead), ...asArray(this.$refs.rbeAddressTail)];
      if (!els.length) return;
      const overflowing = els.map(el => el.scrollWidth > el.clientWidth + 1);
      els.forEach((el, i) => el.classList.toggle('rbe-trunc', overflowing[i]));
    },
    getOrderClass(order) {
      // Add null check for order.order
      if (!order.order) {
        return OrderClassNames.DEFAULT;
      }

      // Check for manually corrected orders or other correct conditions
      if (this.isCorrectOrder(order.order)) {
        return OrderClassNames.CORRECT;
      }

      // If proActivated is false, consider orders with photos as "correct"
      if (this.restaurant && this.restaurant.proActivated === false && order.photoCount > 0) {
        return OrderClassNames.CORRECT;
      }

      // Photos taken but no verdict yet, and we can't analyse right now (offline): this order
      // is PENDING, not wrong. Must be checked before isIncorrectOrder, which would otherwise
      // paint any photographed-but-unanalysed order orange.
      if (this.isPendingAnalysis(order)) {
        return OrderClassNames.PENDING;
      }

      const orderAnalysis = order.order.orderAnalyses;

      if (this.isIncorrectOrder(order, orderAnalysis)) {
        return OrderClassNames.INCORRECT;
      }

      return OrderClassNames.DEFAULT;
    },
    isPendingAnalysis(order) {
      if (!order.order || !(order.photoCount > 0)) return false;
      if (this.isCorrectOrder(order.order)) return false;
      const analysis = order.order.orderAnalyses;
      // isCorrect === false only counts with an appResponse: the CV inserts the row with the
      // DEFAULT false before any comparison, which is still "no verdict" (see verdictState.js).
      const decided = isDecidedAnalysis(analysis, order.order.manualIsCorrect);
      if (decided) return false; // a real verdict exists → correct/incorrect handles it
      // Photos + no verdict. Badge it as "pending" only when we know we're offline (the reason
      // it hasn't analysed) — the analysis relaunches automatically on reconnect. When online
      // it's a transient in-flight state, so we leave the existing behaviour untouched.
      return typeof this.isOnline === 'function' ? this.isOnline() === false : false;
    },
    isCorrectOrder(orderData) {
      // Case 1: orderData is null
      if (!orderData) {
        return false;
      }

      // Case 2: Check for manual correction first
      if (orderData.manualIsCorrect === true) {
        return true;
      }

      // Case 3: Check orderAnalyses
      const orderAnalysis = orderData.orderAnalyses;
      if (!orderAnalysis) {
        return false;
      }

      // Case 4: Has app response and is either analyzed correctly or empty order content
      if (orderAnalysis.appResponse) {
        return (
          (orderAnalysis.isCorrect === true &&
          orderAnalysis.appResponse === AppResponseTypes.ORDER_ANALYZED) ||
          orderAnalysis.appResponse === AppResponseTypes.EMPTY_ORDER_CONTENT
        );
      }

      // Case 5: No app response but marked as correct
      return orderAnalysis.isCorrect === true;
    },
    isIncorrectOrder(order, orderAnalysis) {
      const hasPhotos = order.photoCount > 0;
      
      // Case 1: if orderAnalysis is null we return true if there are photos
      if (!orderAnalysis) {
        return hasPhotos;
      }

      // Decided wrong only: the provisional row (isCorrect false, no appResponse) is no verdict.
      const hasIncorrectAnalysis = isIncorrectAnalysis(orderAnalysis);
      const isEmptyModelDetections = orderAnalysis.appResponse === AppResponseTypes.EMPTY_MODEL_DETECTIONS;

       // Case 2: If has app response, check all conditions
       if (orderAnalysis.appResponse) {
        return hasIncorrectAnalysis || hasPhotos || isEmptyModelDetections;
      }

      // Case 3: No app response, check basic conditions
      return hasIncorrectAnalysis || hasPhotos;
    },
    getMinutesAgo(createdAt) {
      if (!createdAt) return 0;

      try {
        // Treat timestamps without timezone info as UTC
        let timestamp = createdAt;
        if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(timestamp)) {
          timestamp += 'Z';
        }
        const created = new Date(timestamp);
        // Use the reactive currentTime property instead of creating a new Date
        const now = this.currentTime;
        
        // Calculate the difference in minutes
        const diffInMinutes = Math.floor((now - created) / (1000 * 60));

        return Math.max(0, diffInMinutes);
      } catch (error) {
        logger.error('Error calculating time difference', {error: error});
        return 0;
      }
    },
    // Continuous elapsed in whole seconds (no minute-floor) against the same reactive currentTime, for
    // the visibility thresholds. getMinutesAgo floors to whole minutes, so `<= 1` would keep a done
    // order until 2:00 instead of 1:00; the backend (get_raw_orders_realtime) anchors on continuous
    // timestamps, so the filter must too, or the tablet lags the backend by up to a minute on every
    // clock. getMinutesAgo stays as-is for the human-readable "hace N min" labels.
    getSecondsAgo(ts) {
      if (!ts) return 0;
      try {
        let timestamp = ts;
        if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(timestamp)) {
          timestamp += 'Z';
        }
        return Math.max(0, Math.floor((this.currentTime - new Date(timestamp)) / 1000));
      } catch (error) {
        logger.error('Error calculating time difference (s)', {error: error});
        return 0;
      }
    },
    getTimeAgo(createdAt) {
      const diffInMinutes = this.getMinutesAgo(createdAt);
      
      if (diffInMinutes === 0) {
        return this.$t('message.justNow');
      } else if (diffInMinutes === 1) {
        return this.$t('message.minuteAgo');
      } else {
        return this.$t('message.minutesAgo', { minutes: diffInMinutes });
      }
    },
    rbeTimeAgo(createdAt) {
      // RBE pilot: compact time badge so it doesn't steal width from the address/name column. The full
      // "Hace un momento" / "Hace N min" strings pushed the address into an early truncation (verified
      // on burgerking-villanueva). Here <1 min shows "<1m" and the rest "Nm" (e.g. "3m", "12m").
      const m = this.getMinutesAgo(createdAt);
      return m < 1 ? '<1m' : m + 'm';
    },
    handleTicketItemClick(itemId) {
      // You can handle the clicked item here if needed
      logger.console('handleTicketItemClick - Ticket item clicked: ' + itemId);
    },
    hasRiderArrived(order) {
      return order.order?.orderEvents?.some(e => e.event === 'RIDER_CHECK_IN') || false;
    },
    getRiderArrivalTime(order) {
      const event = order.order?.orderEvents?.find(e => e.event === 'RIDER_CHECK_IN');
      if (!event?.registeredAt) return null;
      // Treat timestamps without timezone info as UTC
      const ts = event.registeredAt;
      if (typeof ts === 'string' && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts)) {
        return ts + 'Z';
      }
      return ts;
    },
    getRiderMinutesWaiting(order) {
      const arrivalTime = this.getRiderArrivalTime(order);
      if (!arrivalTime) return 0;
      return this.getMinutesAgo(arrivalTime);
    },
    getRiderWaitingTime(order) {
      const diffInMinutes = this.getRiderMinutesWaiting(order);
      if (diffInMinutes < 1) return '';
      if (diffInMinutes === 1) return '1 min';
      return `${diffInMinutes} min`;
    },
  }
};
</script>
<style scoped>
.main-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.summary-section {
  position: relative;
  width: 100%;
  background-color: var(--bz-p-0c1117);
  padding: 8px 8px 12px 8px;
  box-sizing: border-box;
  height: auto;
  min-height: 80px;
  max-height: 120px;
  z-index: 10;
  margin-bottom: 0;
}

.separator-line {
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, var(--bz-p-blue-400-a20), var(--bz-p-blue-400-a70), var(--bz-p-blue-400-a20));
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 11;
  box-shadow: 0px 1px 2px var(--bz-p-black-a10);
}

.orders-container {
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;
  margin-top: 0;
}

.orders-scroll-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, 325px);
  grid-template-rows: repeat(2, auto);
  grid-gap: 16px;
  justify-content: start;
  overflow-x: auto;
  overflow-y: auto;
  padding: 16px;
  background: linear-gradient(90deg, var(--bz-p-black-a10) 0%, var(--bz-p-black-a2) 50%, var(--bz-p-black-a6) 100%);
  backdrop-filter: blur(12px);
  height: 100%;
  max-height: calc(100vh - 180px);
  max-height: calc(100dvh - 180px);
}

.orders-scroll-container::-webkit-scrollbar {
  display: none;
}

.order-button {
  background: linear-gradient(var(--bz-p-blue-400), var(--bz-p-blue-400)) padding-box, linear-gradient(to bottom right, var(--bz-p-83baff), var(--bz-p-3885e3)) border-box;
  border-radius: 16px;
  border: 1px solid transparent;
  margin: 0;
  width: 325px;
  height: 455px; /* Reduced to approximately 70% of original 650px */
  flex: 0 0 325px;
  color: var(--bz-p-0c1117);
  text-align: center;
  font-family: "DM Sans";
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  box-shadow: 0px 0px 16px 0px var(--bz-p-0c1117-a30);
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 8px;
  box-sizing: border-box;
  min-height: 100px;
}

.order-button.correct-order {
  background: linear-gradient(var(--bz-p-green-400), var(--bz-p-green-400)) padding-box, 
  linear-gradient(to bottom right, var(--bz-p-green-500), var(--bz-p-green-300)) border-box;
}

.order-button.incorrect-order {
  background: var(--bz-p-e4a556);
}

/* Photos taken, not analysed yet (offline). A calm slate — clearly NOT the orange "wrong"
   state — with an alert badge so staff read it as "waiting to analyse", not "failed". */
.order-button.pending-order {
  background: linear-gradient(var(--bz-p-slate-400), var(--bz-p-slate-400)) padding-box,
  linear-gradient(to bottom right, var(--bz-p-slate-300), var(--bz-p-slate-500)) border-box;
}

.order-pending-badge {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 3;
  color: var(--bz-ink--slate-700);
  font-size: 24px;
  line-height: 1;
  /* Center the glyph in a fixed box so PrimeIcons' own line metrics don't push it off-center
     inside the badge — was visibly off on mobile. */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}
.order-pending-badge .pi {
  display: block;
  line-height: 1;
}

.order-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
}

/* Bottom info row: time-ago + photo count aligned at same height */
.order-bottom-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: absolute;
  bottom: 0;
}

.order-bottom-info .time-ago,
.order-bottom-info .photo-count {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 33px;
  white-space: nowrap;
}

.photo-count {
  background-color: var(--bz-p-bfdbff);
  color: var(--bz-p-0c1117);
  text-align: center;
}
.photo-count.incorrect-order {
  background-color:var(--bz-p-fcdcb5);
}

.photo-count.correct-order {
  background-color:var(--bz-p-a3f8bc);
}

.order-id-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 0 0 0;
  margin: 0;
  overflow: hidden;
}

.order-id {
  font-size: 40px;
  font-weight: 700;
  width: max-content;
  text-align: center;
  white-space: nowrap;
  transform-origin: center center;
  transform: scale(var(--scale, 1));
  margin: 0;
  padding: 0;
}

.platform-icon {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 24px;
  height: 24px;
  object-fit: contain;
  border-radius: 16px;
}

/* Desktop/Tablet styles for platform-icon */
@media (min-width: 768px) {
  .platform-icon {
    top: -6px;
    left: -6px;
  }
}

/* Rider waiting state — red border for visibility (no pulsing glow until urgent) */
.order-button.rider-waiting {
  border: 2px solid var(--bz-p-red-600) !important;
}

/* Urgent (a rider waiting ≥10 min) no longer pulses: norm 1, nothing loops beside the photo
   (docs/sistema-diseno/forma-y-tamano.md, step 1). The red border says it, fixed. */

/* Rider arrival badge — flush with button's inner top-right corner.
   Sits at top:0 right:0 of .order-content (which fills the button padding area).
   outer-radius matches the button inner radius so curves blend seamlessly.
   Button border-radius 16px − padding 8px = inner visual radius ~8px. */
.rider-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: var(--bz-p-red-600);
  color: var(--bz-p-white);
  border-radius: 0 14px 0 10px;
  padding: 5px 10px 5px 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  font-family: "DM Sans", sans-serif;
  z-index: 10;
  white-space: nowrap;
  line-height: 1;
  /* Safety net: never let a long label (e.g. long locales like FR) overflow the card */
  max-width: calc(100% - 16px);
  box-sizing: border-box;
}

.rider-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.rider-label {
  font-weight: 700;
  /* Let the label (least critical part) ellipsize before the badge overflows */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rider-wait-time {
  opacity: 0.85;
  font-weight: 600;
  border-left: 1px solid var(--bz-p-white-a40);
  padding-left: 4px;
  flex-shrink: 0;
}
.time-ago {
  color: var(--bz-p-0c1117);
}
.time-ago.time-delay {
  background-color: var(--bz-p-ffd400);
  color: var(--bz-p-0c1117);
  padding-left:5px;
  padding-right:5px;
  border-radius: 33px;
  font-weight: 600; 
}

.brand-name {
  font-size: 14px;
  font-weight: 600;
  margin-top: 16px;
  margin-bottom: 8px;
}

.no-orders-message {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: var(--bz-p-white);
  font-size: 35px;
  font-weight: 500;
  text-align: center;
  padding: 30px;
}

/* Mobile styles */
@media (max-width: 767px) and (orientation: portrait) {
  .summary-section {
    padding: 6px;
    min-height: 50px;
    max-height: 80px;
  }

  .orders-container {
    height: auto;
  }

  .orders-scroll-container {
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    height: auto;
    white-space: normal;
    padding: 8px;
    padding-bottom: 80px;
  }

  .order-button {
    width: calc(100% - 20px);
    height: auto;
    min-height: 62px;
    margin: 10px;
    padding: 10px 15px;
    flex: none;
  }

  .order-id {
    font-size: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .order-content {
    height: auto;
  }

  .order-id-wrapper {
    height: auto;
    padding: 0;
    justify-content: center;
    gap: 6px;
  }

  .order-bottom-info {
    position: static;
  }

  .order-bottom-info .time-ago,
  .order-bottom-info .photo-count {
    font-size: 10px;
    padding: 2px 6px;
  }

  .separator-line {
    height: 5px;
    background: linear-gradient(90deg, var(--bz-p-blue-400-a30), var(--bz-p-blue-400-a60), var(--bz-p-blue-400-a30));
  }

  .no-orders-message {
    font-size: 16px;
    padding: 20px;
  }

  .rider-badge {
    top: 0;
    right: 0;
    padding: 3px 8px 3px 6px;
    font-size: 10px;
    gap: 3px;
    border-radius: 0 14px 0 8px;
  }

  .rider-icon {
    width: 12px;
    height: 12px;
  }
}

/* Classic Layout Styles */
.classic-layout {
  width: 100%;
  position: absolute;
  bottom: 0;
  border-radius: 24px 24px 0 0;
  overflow: hidden;
  border-top: 1px solid var(--bz-p-white-a10);
}

.classic-layout .orders-scroll-container {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-top: 16px;
  padding-bottom: 16px;
  background: linear-gradient(90deg, var(--bz-p-black-a10) 0%, var(--bz-p-black-a2) 50%, var(--bz-p-black-a6) 100%);
  backdrop-filter: blur(12px);
  height: auto;
  max-height: none;
  grid-template-columns: none;
  grid-template-rows: none;
  grid-gap: 0;
  justify-content: flex-start;
}

.classic-layout .orders-scroll-container::-webkit-scrollbar {
  display: none;
}

.classic-layout .order-button {
  background: linear-gradient(var(--bz-p-blue-400), var(--bz-p-blue-400)) padding-box, linear-gradient(to bottom right, var(--bz-p-83baff), var(--bz-p-3885e3)) border-box;
  border-radius: 16px;
  border: 1px solid transparent;
  margin-left: 16px;
  width: 180px;
  height: 100px;
  flex: 0 0 180px;
  color: var(--bz-p-0c1117);
  text-align: center;
  font-family: "DM Sans";
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  box-shadow: 0px 0px 16px 0px var(--bz-p-0c1117-a30);
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 8px;
  box-sizing: border-box;
}

.classic-layout .order-button.add-manual-order-button {
  background: linear-gradient(var(--bz-p-slate-600), var(--bz-p-slate-600)) padding-box,
    linear-gradient(to bottom right, var(--bz-p-slate-500), var(--bz-surface--slate-700)) border-box !important;
  border: 1px solid transparent !important;
  overflow: hidden;
}

.classic-layout .order-button.correct-order {
  background: linear-gradient(var(--bz-p-green-400), var(--bz-p-green-400)) padding-box, 
  linear-gradient(to bottom right, var(--bz-p-green-500), var(--bz-p-green-300)) border-box;
}

.classic-layout .order-button.incorrect-order {
  background: var(--bz-p-e4a556);
}

.classic-layout .order-button.pending-order {
  background: linear-gradient(var(--bz-p-slate-400), var(--bz-p-slate-400)) padding-box,
  linear-gradient(to bottom right, var(--bz-p-slate-300), var(--bz-p-slate-500)) border-box;
}

.classic-layout .order-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
}

.classic-layout .order-id-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.classic-layout .order-id {
  font-size: 40px;
  font-weight: 700;
  width: max-content;
  text-align: center;
  white-space: nowrap;
  transform-origin: center center;
  transform: scale(var(--scale, 1));
  margin: 0;
  padding: 0;
}

.classic-layout .platform-icon {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 24px;
  height: 24px;
  object-fit: contain;
  border-radius: 16px;
}

.classic-layout .order-bottom-info .time-ago,
.classic-layout .order-bottom-info .photo-count {
  font-size: 12px;
  padding: 3px 7px;
}

.classic-layout .rider-badge {
  top: 0;
  right: 0;
  padding: 3px 8px 3px 6px;
  font-size: 10px;
  gap: 3px;
  border-radius: 0 14px 0 10px;
}

.classic-layout .rider-icon {
  width: 12px;
  height: 12px;
}

.classic-layout .order-button.rider-waiting {
  border: 2px solid var(--bz-p-red-600) !important;
}

/* Add manual order button */
.add-manual-order-button {
  background: linear-gradient(var(--bz-p-slate-600), var(--bz-p-slate-600)) padding-box,
    linear-gradient(to bottom right, var(--bz-p-slate-500), var(--bz-surface--slate-700)) border-box !important;
  border: 1px solid transparent !important;
  cursor: pointer;
  overflow: hidden;
}

.add-manual-content {
  gap: 6px;
}

.add-manual-icon {
  font-size: 28px;
  color: var(--bz-p-white);
}

.add-manual-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--bz-p-white);
  white-space: normal;
  line-height: 1.3;
  text-align: center;
  max-width: 100%;
  word-wrap: break-word;
}

.add-manual-order-button:active {
  background: linear-gradient(var(--bz-p-slate-500), var(--bz-p-slate-500)) padding-box,
    linear-gradient(to bottom right, var(--bz-p-slate-400), var(--bz-p-slate-600)) border-box !important;
}

/* Mobile styles for classic layout */
@media (max-width: 767px) and (orientation: portrait) {
  .classic-layout {
    height: 65%;
  }

  .classic-layout .orders-scroll-container {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100%;
    white-space: normal;
    padding-bottom: 20px;
    box-sizing: border-box;
  }

  .classic-layout .order-button {
    width: calc(100% - 20px);
    height: 70px;
    margin: 10px;
    padding: 5px 15px;
    flex: none;
  }

  .classic-layout .order-content {
    flex: 1;
  }

  .classic-layout .order-id-wrapper {
    flex: 1;
    justify-content: center;
    gap: 2px;
  }

  .classic-layout .order-bottom-info {
    position: static;
  }

  .classic-layout .platform-icon {
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  .classic-layout .order-id {
    font-size: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .classic-layout .order-bottom-info .time-ago,
  .classic-layout .order-bottom-info .photo-count {
    font-size: 10px;
    padding: 2px 6px;
  }
}

/* Mobile: add-manual button horizontal layout (applies to both KDS and Classic) */
@media (max-width: 767px) and (orientation: portrait) {
  .add-manual-content {
    flex-direction: row !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 10px !important;
  }

  .add-manual-icon {
    font-size: 22px;
  }

  .add-manual-label {
    font-size: 16px;
  }
}

/* ── RBE pilot (behind `rbe-order-button` flag): dedicated left-aligned button layout ──
   [icon] [code / name]  ················  [time / photos stacked]
   Scoped to `.rbe-order` / `.rbe-*` so other clients are untouched. No rider elements (RBE has none). */
.order-button.rbe-order {
  height: auto;
  min-height: 78px;
  padding: 12px 14px;
}

.rbe-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.rbe-platform-icon {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 12px;
}

.rbe-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  text-align: left;
}

.rbe-code {
  font-size: 25px;
  font-weight: 700;
  line-height: 1.05;
  color: var(--bz-p-0c1117);
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rbe-name {
  display: flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
  color: var(--bz-p-0c1117);
}

.rbe-person-icon {
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  opacity: 0.7;
}

.rbe-name span {
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Burger King address variant: location-pin icon + two-part text. The street (head) shrinks first and
   fades out on the right when it overflows; the house number (tail) shrinks last and its start — the
   portal number — is always visible (it fades on the right too, never on the left). The fade replaces
   an ellipsis so the number sits flush with no gap. `.rbe-trunc` is toggled by adjustAddressFades(). */
.rbe-address {
  display: flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
  color: var(--bz-p-0c1117);
}

.rbe-pin-icon {
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  opacity: 0.7;
}

.rbe-address-text {
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;              /* backstop: never paint over the time/photos column */
  font-size: 16px;
  font-weight: 600;
  line-height: 1.15;
}

.rbe-address-head {
  flex: 0 1000 auto;             /* absorbs almost all of the shrink → the house number stays intact */
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.rbe-address-tail {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 62%;                /* a very long tail can't swallow the street; portal stays at the start */
  overflow: hidden;
  white-space: pre;
}

.rbe-address-head.rbe-trunc,
.rbe-address-tail.rbe-trunc {
  -webkit-mask-image: linear-gradient(to right, var(--bz-p-black) calc(100% - 22px), transparent);
  mask-image: linear-gradient(to right, var(--bz-p-black) calc(100% - 22px), transparent);
}

.rbe-meta {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.rbe-time,
.rbe-photos {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 33px;
  white-space: nowrap;
}

.rbe-time { color: var(--bz-p-0c1117); }
.rbe-time.time-delay { background-color: var(--bz-p-ffd400); color: var(--bz-p-0c1117); }
.rbe-photos { background-color: var(--bz-p-bfdbff); color: var(--bz-p-0c1117); }
.rbe-photos.correct-order { background-color: var(--bz-p-a3f8bc); }
.rbe-photos.incorrect-order { background-color: var(--bz-p-fcdcb5); }
/* ════ The service screen on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   docs/sistema-diseno/forma-y-tamano.md («Bandeja», «La tarjeta de pedido») on the approved palette
   (paleta-bronze.md). Everything above stays as production paints it, for the stores without the flag;
   these rules win on specificity (`:root[data-bz-lane]`), and with `!important` only where production
   already used it. The classic layout only: the KDS layout is not in use, and the RBE pilot keeps its
   own layout, flat on the palette. */

/* The tray: flat, the background's colour, a hairline on top; no blur, no corners, no shadow. */
:root[data-bz-lane="system"] .classic-layout {
  border-radius: 0;
  border-top: 1px solid var(--bz-edge);
  background: var(--bz-bg);
}

:root[data-bz-lane="system"] .classic-layout .orders-scroll-container {
  gap: var(--bz-space-4);
  padding: var(--bz-space-4) var(--bz-gutter);
  background: none;
  backdrop-filter: none;
  overscroll-behavior: contain;
}

/* A card: flat fill of its state with the dark ink every card shares, no bevel, no shadow. */
:root[data-bz-lane="system"] .classic-layout .order-button {
  margin: 0;
  border: 0;
  border-radius: var(--bz-radius-lg);
  background: var(--bz-todo-solid);
  color: var(--bz-on-todo-solid);
  box-shadow: none;
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .classic-layout .order-button.correct-order {
  background: var(--bz-ok-solid);
  color: var(--bz-on-ok-solid);
}

:root[data-bz-lane="system"] .classic-layout .order-button.incorrect-order {
  background: var(--bz-wrong-solid);
  color: var(--bz-on-wrong-solid);
}

:root[data-bz-lane="system"] .classic-layout .order-button.pending-order {
  background: var(--bz-pending-solid);
  color: var(--bz-on-pending-solid);
}

/* A rider waiting: a ring in the rider's red on the page, 2 px off the card, so it reads around a card of
   any colour (paleta-bronze.md, rule 4); the band at the foot says who and how long. Production's own
   2-px border carried `!important`, hence this one. */
:root[data-bz-lane="system"] .classic-layout .order-button.rider-waiting {
  border: 0 !important;
  outline: 3px solid var(--bz-rider-border);
  outline-offset: 2px;
}

/* Three rows, the same on every card: 24 at the top, the code, 24 at the foot — and 8 under the foot, so
   «the clock» (and the late pill around it) never sits on the card's edge (Adrián, 13-oct-2026: it looked
   stuck to the bottom). The rider's band takes those 8 back and stays flush with the edge. */
:root[data-bz-lane="system"] .classic-layout .order-button:not(.rbe-order):not(.add-manual-order-button) {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: var(--bz-space-6) minmax(0, 1fr) var(--bz-space-6);
  align-items: stretch;
  justify-items: stretch;
  padding: var(--bz-space-1) var(--bz-space-2) var(--bz-space-2);
  text-align: center;
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

.card-platform {
  width: var(--bz-icon-sm);
  height: var(--bz-icon-sm);
  object-fit: contain;
  border-radius: var(--bz-radius-full);
}

.card-state {
  margin-left: auto;
  font-size: var(--bz-icon-sm);
}

/* The code: the card's reason to exist, at 40/44 and 700. A long one shrinks to fit, as in production
   (adjustFontSizes() sets `--scale`). */
:root[data-bz-lane="system"] .classic-layout .card-code {
  height: auto;
  min-width: 0;
  justify-content: center;
}

:root[data-bz-lane="system"] .classic-layout .card-code .order-id {
  font-size: var(--bz-text-code);
  line-height: var(--bz-leading-code);
  letter-spacing: var(--bz-tracking-code);
  font-weight: var(--bz-weight-strong);
}

/* «The clock»: what is read to decide, at 14 and never less (norm 9). */
.card-clock,
.card-rider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-1);
  min-width: 0;
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
  white-space: nowrap;
}

/* The photos: the camera and how many, never cut. Written out («· 1 foto») it did not fit beside «Hace un
   momento» on the Honor's card (171 px) and lost the count to an ellipsis; production's card shows it whole
   (measured on the tablet, 14-oct-2026). The words stay for a screen reader. */
.card-photos {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--bz-space-1);
  margin-left: var(--bz-space-1);
}

/* If anything has to give on a narrow card, the time does, not the count. */
.card-time {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-photos .bz-icon {
  font-size: var(--bz-icon-xs);
}

/* Late (over 10 min): a dark capsule — the app's background — with the clock and the time in the delay's
   yellow. It reads the same on a card of any colour, with nothing to cut it out from the fill (14-oct-2026:
   the yellow pill with its black ring clashed on the orange card, Adrián). */
.card-time.is-late {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-1);
  padding: 0 var(--bz-space-2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-bg);
  color: var(--bz-warn);
}

.card-time.is-late .bz-icon {
  font-size: var(--bz-icon-xs);
}

/* The state of a card that shows no mark, named for a screen reader only. */
.card-state-name {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* The rider's band: the foot row turns red from edge to edge, its corners those of the card. */
.card-rider {
  align-self: end;
  box-sizing: border-box;
  height: var(--bz-space-6);
  margin: 0 calc(-1 * var(--bz-space-2)) calc(-1 * var(--bz-space-2));
  padding: 0 var(--bz-space-2);
  border-radius: 0 0 var(--bz-radius-lg) var(--bz-radius-lg);
  background: var(--bz-rider-solid);
  color: var(--bz-on-rider-solid);
}

.card-rider .bz-icon {
  font-size: var(--bz-icon-xs);
}

.card-rider-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* «Add a manual order»: a card of the system's own grey, its edge and its words. */
:root[data-bz-lane="system"] .classic-layout .order-button.add-manual-order-button {
  border: 1px solid var(--bz-edge) !important;
  background: var(--bz-surface) !important;
  color: var(--bz-ink);
}

:root[data-bz-lane="system"] .add-manual-icon {
  font-size: var(--bz-icon-md);
  color: var(--bz-ink);
}

:root[data-bz-lane="system"] .add-manual-label {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
}

/* The RBE pilot keeps its layout, flat on the palette: its ink is the cards' own. */
:root[data-bz-lane="system"] .rbe-code,
:root[data-bz-lane="system"] .rbe-name,
:root[data-bz-lane="system"] .rbe-address {
  color: inherit;
}

/* The phone, upright: each card a row of 72, the code at the start and the clock at the end. */
@media (max-width: 767px) and (orientation: portrait) {
  :root[data-bz-lane="system"] .classic-layout .orders-scroll-container {
    padding: var(--bz-space-4) var(--bz-gutter);
  }

  :root[data-bz-lane="system"] .classic-layout .order-button {
    width: 100%;
    height: var(--bz-target-primary);
    margin: 0;
  }

  :root[data-bz-lane="system"] .classic-layout .order-button:not(.rbe-order):not(.add-manual-order-button) {
    grid-template-rows: 1fr;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--bz-space-3);
    padding: 0 var(--bz-space-4);
  }

  .card-top {
    gap: var(--bz-space-2);
  }

  :root[data-bz-lane="system"] .classic-layout .card-code {
    align-items: flex-start;
  }

  :root[data-bz-lane="system"] .classic-layout .card-code .order-id {
    font-size: var(--bz-text-3xl);
    line-height: var(--bz-leading-3xl);
    transform-origin: left center;
  }

  .card-rider {
    margin: 0;
    padding: var(--bz-space-1) var(--bz-space-3);
    border-radius: var(--bz-radius-full);
  }
}
</style>